/**
 * map-context.js
 */

import React from 'react';
import { Loader } from "@googlemaps/js-api-loader";
import analytics from 'utils/analytics';
import { UserContext } from 'contexts/user/user-context';
import { TrackContext } from '../track-context';
import { API_KEY } from '../tracking-common';
import {
  MAP_TYPE,
  snappedMarkerFromPoint,
  generateSnappedPoint,
  processPhotoFile,
  getMarker,
} from './map-common';


export const MapContext = React.createContext();


export const MapProvider = (props) => {
  const { user } = React.useContext(UserContext);
  const { persistenceManager, tracks, updateTrack } = React.useContext(TrackContext);
  const [state, setState] = React.useState({
    mapApi: null,
    baseContainer: null,
    map: null,
    mapType: MAP_TYPE.SATELLITE,
    selectedPoint: null,
    selectedMarkerUuid: null,
  });


  // init - load map libs
  React.useEffect(() => {
    (async() => {
      const loader = new Loader({ apiKey: API_KEY, version: 'weekly' });
      // const loader = new Loader({ version: 'weekly' }); // DEV loader doesn't use api key
      await loader.load();
      const { Map } = await window.google.maps.importLibrary('maps');
      const { AdvancedMarkerElement, PinElement } = await window.google.maps.importLibrary('marker');
      setState(p => ({
        ...p,
        mapApi: {
          Map,
          Marker: AdvancedMarkerElement,
          PinElement: PinElement,
          Line: window.google.maps.Polyline,
          Point: window.google.maps.Point,
          Bounds: window.google.maps.LatLngBounds,
          Event: window.google.maps.event,
        },
      }));
    })();
  }, []);



  // -----------------------------------------------------------------------------------------
  // private methods
  // -----------------------------------------------------------------------------------------


  const updateTrackMarkers = React.useCallback((track, markers) => {
    updateTrack(track.id, { markers });
  }, [updateTrack]);



  // -----------------------------------------------------------------------------------------
  // public methods - map
  // -----------------------------------------------------------------------------------------


  // used to keep track of main map container so dialog can take map and put it back
  const setBaseContainer = React.useCallback((baseContainer) => {
    setState(p => ({
      ...p,
      baseContainer
    }));
  }, []);


  const setMap = React.useCallback((map) => {
    setState(p => ({
      ...p,
      map,
    }));
  }, []);


  const setMapType = React.useCallback((mapType) => {
    setState(p => ({
      ...p,
      mapType
    }));
  }, []);



  // -----------------------------------------------------------------------------------------
  // public methods - markers
  // -----------------------------------------------------------------------------------------


  // adds marker to active track at given coordinates
  // (assume this is a quick pin, because that's currently the only way to add a marker directly,
  // i.e. without saving a previously selected point)
  const addMarker = React.useCallback(async (lat, lng, photoFile) => {
    let newMarker = null;
    const track = tracks.find(t => t.isActive);

    if (track) {
      newMarker = snappedMarkerFromPoint(generateSnappedPoint(track, lat, lng), user);
      if (newMarker) {
        newMarker.isQuickPin = true;

        const processedPhotoFile = photoFile ? await processPhotoFile(photoFile) : null;

        if (processedPhotoFile) {
          newMarker.photoPreview = URL.createObjectURL(processedPhotoFile);
        }

        persistenceManager.queueMarker(track, {
          ...newMarker,
          photoFile: processedPhotoFile,
        });

        const markers = [ ...track.markers, newMarker ];

        analytics.trackGA4('event', {
          name: 'track_pin_create', params: {
            trackpin_track_type: track.type,
            trackpin_mode: photoFile ? 'quick_photo' : 'quick',
            trackpin_phase: 'during',
            trackpin_user_role: user
              ? user.role.name.toLowerCase()
              : 'anonymous'
          }
        });

        setState(p => ({ 
          ...p, 
          selectedMarkerUuid: newMarker.uuid, 
        }));
        updateTrackMarkers(track, markers);
      }
    }
  }, [user, tracks, persistenceManager, updateTrackMarkers]);


  // creates a new selected point object and sets it in the state.
  // this object includes a trackIdx property which points to the active track 
  // if it exists or uses the closest track if there is none.
  const selectPoint = React.useCallback((e) => {
    let newSelectedPoint = null;
    let snappedPoint = null;
    let trackIdx = tracks.findIndex(t => t.isActive);

    // use the active track if there is one, otherwise whichever track is closest
    if (trackIdx !== -1) {
      snappedPoint = generateSnappedPoint(tracks[trackIdx], e.latLng.lat(), e.latLng.lng())
    } else {
      tracks.forEach((t, i) => {
        const testSnappedPoint = generateSnappedPoint(t, e.latLng.lat(), e.latLng.lng())
        if (!snappedPoint || testSnappedPoint.properties.dist < snappedPoint.properties.dist) {
          snappedPoint = testSnappedPoint;
          trackIdx = i;
        }
      });
    }

    if (snappedPoint) {
      newSelectedPoint = snappedMarkerFromPoint(snappedPoint, user);
      if (newSelectedPoint) {
        newSelectedPoint.trackIdx = trackIdx;
      }
    }

    setState(p => ({
      ...p,
      selectedPoint: newSelectedPoint,
      selectedMarkerUuid: null,
    }));
  }, [user, tracks]);


  const deselectPoint = React.useCallback(() => {
    setState(p => ({
      ...p,
      selectedPoint: null,
    }))
  }, []);


  // the state's selected point member object has a previously-saved trackIdx property specifying the
  // track that this selected point is attached to.
  const saveSelectedPoint = React.useCallback(() => {
    if (!state.selectedPoint) {
      return;
    }

    // NOTE: we're assuming that nothing can have happened to change the order of tracks
    // since the point was selected. If a process can alter those,
    // then it should also discard the selected point before we get here.

    const { trackIdx, ...marker } = state.selectedPoint;
    marker.isQuickPin = false;
    const track = tracks[trackIdx];
    if (track) {
      persistenceManager.queueMarker(track, marker);
      const markers = [ ...track.markers, marker ];
  
      analytics.trackGA4('event', {
        name: 'track_pin_create', params: {
          trackpin_track_type: track.type,
          trackpin_mode: 'standard',
          trackpin_phase: track.isActive ? 'during' : 'after',
          trackpin_user_role: user
            ? user.role.name.toLowerCase()
            : 'anonymous'
        }
      });

      setState(p => ({
        ...p,
        // deselect selectd point
        selectedPoint: null,
        // autoselect new marker
        selectedMarkerUuid: marker.uuid,
      }));
      updateTrackMarkers(track, markers);
    }
  }, [state.selectedPoint, tracks, persistenceManager, updateTrackMarkers, user]);


  const selectMarker = React.useCallback((uuid) => {
    setState(p => ({
      ...p,
      selectedMarkerUuid: uuid,
      selectedPoint: null,
    }))
  }, []);


  const deselectMarker = React.useCallback(() => {
    setState(p => ({
      ...p,
      selectedMarkerUuid: null,
    }))
  }, []);


  const updateMarker = React.useCallback(async (track, markerUuid, data, photoFile) => {
    const marker = {
      ...getMarker(track, markerUuid),
      ...data,
    };

    const processedPhotoFile = photoFile ? await processPhotoFile(photoFile) : null;

    if (processedPhotoFile) {
      marker.photoPreview = URL.createObjectURL(processedPhotoFile);
    }

    persistenceManager.queueMarker(track, {
      ...marker, 
      photoFile: processedPhotoFile,
    });

    const markers = track.markers.map(m => m.uuid === markerUuid ? marker : m);
    updateTrackMarkers(track, markers);
  }, [persistenceManager, updateTrackMarkers]);


  const deleteMarker = React.useCallback(async (track, markerUuid) => {
    const marker = getMarker(track, markerUuid);
    persistenceManager.deleteMarker(track, marker);
    const markers = track.markers.filter(m => m.uuid !== markerUuid);
    setState(p => ({ 
      ...p, 
      selectedMarkerUuid: markerUuid === p.selectedMarkerUuid ? null : p.selectedMarkerUuid,
    }));
    updateTrackMarkers(track, markers);   
  }, [persistenceManager, updateTrackMarkers]);



  // -----------------------------------------------------------------------------------------
  // final state value
  // -----------------------------------------------------------------------------------------

  const value = React.useMemo(() => ({
    ...state,

    // map
    setBaseContainer,
    setMap,
    setMapType,

    // markers
    addMarker,
    selectPoint,
    deselectPoint,
    saveSelectedPoint,
    selectMarker,
    deselectMarker,
    updateMarker,
    deleteMarker,
  }), [
    state,
    setBaseContainer,
    setMap,
    setMapType,
    addMarker,
    selectPoint,
    deselectPoint,
    saveSelectedPoint,
    selectMarker,
    deselectMarker,
    updateMarker,
    deleteMarker,
  ]);


  return (<>
    <MapContext.Provider 
      value={value}
      children={props.children}
    />
  </>);
}